import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useTypedParams } from 'react-router-typesafe-routes';
import { z } from 'zod';
import FormInput from '@/components/common/form-input';
import ProgressButton from '@/components/common/progress-button';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import useGroupInfo from '@/hooks/groups/use-group-info';
import useUpdateGroupDetails from '@/hooks/groups/use-update-group-details';
import useAdjustLayout from '@/hooks/use-adjust-layout';
import ROUTES from '@/routes';

export default function UpdateGroupInfoRoute() {
  const { id: groupId } = useTypedParams(ROUTES.MESSAGES.GROUPS.SETTINGS.ADMIN.INFO);
  const { data: groupInfo, isLoading: isLoadingGroup, canManage } = useGroupInfo(groupId);
  const { t } = useTranslation();
  const { mutateAsync: updateGroup, isPending: isUpdating } = useUpdateGroupDetails(groupId);

  useAdjustLayout();

  const {
    control,
    handleSubmit,
    formState: { isDirty },
    reset,
  } = useForm<UpdateGroupInfoFormData>({
    disabled: !canManage,
    resolver: zodResolver(updateGroupInfoFormScheme),
    defaultValues: {
      groupName: '',
    },
  });

  useEffect(() => {
    if (groupInfo?.GroupData) {
      reset({
        groupName: groupInfo.GroupData.GroupName,
      });
    }
  }, [groupInfo?.GroupData, reset]);

  const submitUpdate = async (data: UpdateGroupInfoFormData) => {
    if (!canManage) return;

    await updateGroup({
      GroupName: data.groupName,
    });

    reset(data);
  };

  return (
    <PageLayout
      title={canManage ? t('conversation.setting.group.edit-info') : t('conversation.setting.group.info')}
      className='pb-safe-or-4'
      headerConfig={{
        navigateBack: true,
      }}>
      <form
        className='mx-5 mt-4 flex flex-1 flex-col justify-between'
        onSubmit={handleSubmit(submitUpdate, console.error)}>
        <label
          htmlFor=''
          className='form-control w-full'>
          <FormInput
            disabled={isLoadingGroup || isUpdating}
            control={control}
            name='groupName'
            showClearButton
            defaultValue=''
            maxLength={50}
            helperText={t('fields.messages.max-length', { name: t('contact.create-group.group-name'), count: 50 })}
          />
        </label>

        {canManage && (
          <ProgressButton
            as='submit'
            disabled={!isDirty || isLoadingGroup}
            isLoading={isUpdating}
            className='btn btn-primary'
            label='common.confirm'
          />
        )}
      </form>
    </PageLayout>
  );
}

const updateGroupInfoFormScheme = z.object({
  groupName: z.string().trim().min(1, 'errors.string.length|count=1').max(50, 'errors.string.max-length|count=50'),
});

type UpdateGroupInfoFormData = z.infer<typeof updateGroupInfoFormScheme>;
